





























import {
  Vue,
  Component,
  Prop,
} from "@/shared/vuepack";
import { formatCurrency } from "@/shared/currency";
import { GChart } from "vue-google-charts";
import { useThemeStore } from "@/shared/store/theme";
import { useFeatureFlagStore } from "@/shared/store/featureFlag";

@Component({
  components: {
    GChart
  }
})
export default class ContributionsDonutChart extends Vue {
  themeStore = useThemeStore();
  featureFlagStore = useFeatureFlagStore();

  @Prop()
  maxAllowedContributionForCurrentYear: number;

  @Prop()
  employerContribution: number;

  @Prop()
  otherHsaContribution: number;

  @Prop()
  payrollContribution: number;

  @Prop()
  personalContribution: number;

  @Prop()
  bankDirectContribution: number;

  @Prop({ required: false, default: false })
  showForecast: boolean;

  @Prop()
  scheduleForecastedAmount: number;

  @Prop()
  planYear: number;

  formatCurrency = formatCurrency;

  get getChartOptions () {
    return {
      colors: this.colorsToDisplay.map(x => x.chartColor),
      legend: {
        position: "none"
      },
      chartArea: {
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
      },
      pieHole: 0.82,
      pieSliceText: "none",
      enableInteractivity: false,
      tooltip: {
        trigger: "none",
        html: true
      }
    };
  }

  get showChart (): boolean {
    return this.maxAllowedContributionForCurrentYear && this.colorsToDisplay.length > 0;
  }

  get totalContributionAmount (): number {
    return this.personalContribution + this.payrollContribution +
        this.employerContribution + this.otherHsaContribution + this.bankDirectContribution
        + (this.scheduleForecastedAmount || 0);
  }

  get remainingAmount (): number {
    return Math.max(this.maxAllowedContributionForCurrentYear - this.totalContributionAmount, 0);
  }

  get showRemaining (): boolean {
    return this.featureFlagStore.flags.bend17151ContributionWidgetUpdate && this.remainingAmount > 0;
  }

  get orderedAmounts () {
    return [
      {
        label: "Employee",
        amount: this.payrollContribution
      },
      {
        label: "Employer",
        amount: this.employerContribution
      },
      {
        label: "Personal",
        amount: this.personalContribution
      },
      {
        label: "External HSA",
        amount: this.otherHsaContribution
      },
      {
        label: "Contribution",
        amount: this.bankDirectContribution
      }
    ];
  }

  get orderedColors () {
    return [
      {
        chartColor: this.themeStore.primaryColor,
        className: "primary"
      },
      {
        chartColor: this.themeStore.secondaryColor,
        className: "secondary"
      },
      {
        chartColor: this.themeStore.tertiaryColor,
        className: "tertiary"
      },
      {
        chartColor: this.themeStore.primaryColorShifts.lighter,
        className: "primary-lighter"
      },
      {
        chartColor: this.themeStore.secondaryColorShifts.lighter,
        className: "secondary-lighter"
      },
      {
        chartColor: this.themeStore.tertiaryColorShifts.lighter,
        className: "tertiary-lighter"
      }
    ];
  }

  get colorsToDisplay () {
    const colors = this.orderedAmounts.filter(x => x.amount > 0).map((amount, index) => ({
      ...amount,
      ...this.orderedColors[index]
    }));

    // In forecast mode, always show This Schedule, even if amount is 0
    if (this.showForecast) {
      colors.push({
        label: "Scheduled Amount",
        amount: this.scheduleForecastedAmount,
        ...this.orderedColors[colors.length]
      });
    }

    // remaining amount always appears last, as light gray
    if (this.showRemaining) {
      colors.push({
        label: "Remaining",
        amount: this.remainingAmount,
        chartColor: this.themeStore.cobrandingLightGrey,
        className: "remaining"
      });
    }

    return colors;
  }

  get chartData () {
    const items: any[] = [
      ["Type", "Amount", { type: "string", role: "tooltip", p: { html: true } }]
    ];

    for (const color of this.colorsToDisplay) {
      items.push([color.label, color.amount, formatCurrency(color.amount)]);
    }

    return items;
  }
}
